import { LocalizationContext } from "context/localization";
import { useTranslationContext } from "context/translation";
import { FC, useContext } from "react";
import { Image } from "react-datocms/image";
import { CartPageQuery } from "services/datocms/generated";
import { FreeShippingTreshhold } from "./FreeShippingTreshhold";

export interface CartSummaryProps {
  checkoutImages: NonNullable<
    NonNullable<CartPageQuery>["cartPage"]
  >["checkoutPaymentImages"];
  freeShippingThreshold: number;
  shippingFee: number;
  subtotal: number;
  total: number;
}

export const CartSummary: FC<CartSummaryProps> = ({
  checkoutImages,
  freeShippingThreshold,
  shippingFee,
  subtotal,
  total,
}) => {
  const { formatPriceByLocale } = useContext(LocalizationContext);
  const {
    checkoutWith,
    free,
    grandTotal,
    shipping,
    subtotal: subtotalText,
  } = useTranslationContext();

  const isShippingFeeZero = shippingFee === 0;
  let freeShippingProgress = isShippingFeeZero
    ? 100
    : Math.floor((subtotal / freeShippingThreshold) * 100);
  if (freeShippingProgress > 100) freeShippingProgress = 100;

  return (
    <div className="bg-gray-100">
      <div className="container pt-10 pb-12 md:pt-10 md:pb-[100px]">
        <div className="flex flex-col gap-6 divide-y divide-gray-300">
          <div className="flex flex-col gap-4">
            <FreeShippingTreshhold
              freeShippingThreshold={freeShippingThreshold}
              iconClassName="stroke-black"
              subtotal={subtotal}
              textClassName="text-black text-base font-semibold leading-[21px]"
            />
            <div className="h-2 bg-gray-200 rounded-lg border border-gray-300 w-full overflow-hidden">
              <div
                className="h-2 bg-green-400 rounded-lg"
                style={{ width: `${freeShippingProgress}%` }}
              />
            </div>
          </div>
          <div className="flex flex-col gap-4 pt-6">
            <div className="flex justify-between items-center">
              <p className="text-gray-900 text-[15px] font-medium">
                {subtotalText}
              </p>
              <h4 className="text-zinc-900 text-base font-semibold leading-[21px]">
                {formatPriceByLocale(subtotal)}
              </h4>
            </div>
            <div className="flex justify-between items-center">
              <p className="text-gray-900 text-[15px] font-medium">
                {shipping}
              </p>
              <h4 className="text-zinc-900 text-base font-semibold leading-[21px]">
                {isShippingFeeZero ? free : formatPriceByLocale(shippingFee)}
              </h4>
            </div>
          </div>
          <div className="flex justify-between items-center py-6">
            <h2>{grandTotal}</h2>
            <h2>{formatPriceByLocale(total)}</h2>
          </div>
        </div>
        <div className="pt-10 flex flex-col justify-center items-center gap-6">
          <h2>{checkoutWith}</h2>
          <div className="flex gap-1 items-center">
            {checkoutImages.map(
              (image, index) =>
                image.responsiveImage && (
                  <Image
                    className="!w-[70px] !max-w-[70px] !h-[48px] !object-fill"
                    data={image.responsiveImage}
                    key={index}
                  />
                ),
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
